import { useMemo, useRef } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useAcademyChatActions } from '../../context/AcademyChatActionsContext';
import { askAboutUpcomingContent } from '../../utils/askAboutUpcomingContent';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { LearningContentCardsSkeleton } from 'academy-common-lib/components/Skeletons/LearningContentCardsSkeleton';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { LearningContentCard } from 'academy-common-lib/features/learningContentCard/components/LearningContentCard';
import { ACRouterButton } from 'academy-common-lib/components/ACRouterButton';
import { getSearchUrl } from 'academy-common-lib/features/search/searchRouting';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ITEM_TYPE_TO_LEARNING_CONTENT_TYPE = {
  COURSE: 'TRACK',
  LESSON: 'LESSON',
  SHORT_VIDEO: 'SHORT_FORM_CONTENT',
  LEARNING_PATH: 'LEARNING_PATH'
};
const ITEM_TYPE_TO_CATALOG_CONTENT_TYPE = {
  COURSE: 'COURSE',
  LESSON: 'LESSON',
  SHORT_VIDEO: 'SHORT_FORM_CONTENT',
  LEARNING_PATH: 'LEARNING_PATH'
};
const stringQueryValues = values => values === null || values === void 0 ? void 0 : values.filter(value => typeof value === 'string');
const getCatalogSearchCategory = cardModel => {
  if (!('subCategories' in cardModel)) {
    return undefined;
  }
  return stringQueryValues(cardModel.subCategories);
};
const getCatalogSearchTools = cardModel => {
  var _cardModel$hsSoftware, _cardModel$hubSpotSof;
  if ('hsSoftwareFilters' in cardModel && (_cardModel$hsSoftware = cardModel.hsSoftwareFilters) !== null && _cardModel$hsSoftware !== void 0 && _cardModel$hsSoftware.length) {
    return stringQueryValues(cardModel.hsSoftwareFilters);
  }
  if ('hubSpotSoftwareTools' in cardModel && (_cardModel$hubSpotSof = cardModel.hubSpotSoftwareTools) !== null && _cardModel$hubSpotSof !== void 0 && _cardModel$hubSpotSof.length) {
    return stringQueryValues(cardModel.hubSpotSoftwareTools);
  }
  return undefined;
};
const getCatalogContentType = itemType => {
  switch (itemType) {
    case 'COURSE':
    case 'LESSON':
    case 'SHORT_VIDEO':
    case 'LEARNING_PATH':
      return ITEM_TYPE_TO_CATALOG_CONTENT_TYPE[itemType];
    default:
      return undefined;
  }
};
const OnboardingExploreMoreCta = ({
  itemId,
  itemType,
  contentType,
  language,
  cardModel
}) => {
  const {
    onExploreMoreClick
  } = useAcademyChatActions();
  const catalogContentType = getCatalogContentType(itemType);
  if (!catalogContentType) {
    return null;
  }
  return /*#__PURE__*/_jsx(ACRouterButton, {
    alignSelf: "start",
    marginBlockStart: "200",
    responsive: false,
    to: getSearchUrl({
      contentType: catalogContentType,
      language,
      showOnlyCerts: 'certification' in cardModel && cardModel.certification ? true : undefined,
      category: getCatalogSearchCategory(cardModel),
      tool: getCatalogSearchTools(cardModel)
    }),
    use: "link",
    onClick: () => onExploreMoreClick({
      contentId: itemId,
      contentType
    }),
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academy-ai-assistant.onboarding.discoverMoreContent"
    })
  });
};
export const RecommendationCard = ({
  section: {
    itemId,
    itemType,
    language
  }
}) => {
  const {
    onContentCardClick,
    onContentCardImpression,
    isOnboardingFlow
  } = useAcademyChatActions();
  const items = useMemo(() => {
    const type = ITEM_TYPE_TO_LEARNING_CONTENT_TYPE[itemType];
    if (!type) return [];
    return [{
      id: itemId,
      type,
      language: itemType === 'COURSE' || itemType === 'LESSON' ? language === null || language === void 0 ? void 0 : language.toLowerCase() : undefined
    }];
  }, [itemId, itemType, language]);
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError
  } = useLearningContentCardModels({
    items,
    skip: !items.length
  });
  const contentType = ITEM_TYPE_TO_LEARNING_CONTENT_TYPE[itemType];
  const hasTrackedImpressionRef = useRef(false);
  if (cardModelsLoading) {
    return /*#__PURE__*/_jsx(Flex, {
      marginBlock: "400",
      children: /*#__PURE__*/_jsx(View, {
        inlineSize: "100%",
        children: /*#__PURE__*/_jsx(LearningContentCardsSkeleton, {
          skeletonCardSize: "small",
          use: "on-light",
          maxSkeletonListLength: 1
        })
      })
    });
  }
  if (cardModelsError || !(cardModels !== null && cardModels !== void 0 && cardModels.length)) {
    return null;
  }

  // eslint-disable-next-line react-hooks/refs
  if (!hasTrackedImpressionRef.current && contentType) {
    hasTrackedImpressionRef.current = true;
    onContentCardImpression({
      contentId: itemId,
      contentType,
      appearsIn: 'recommendation-card'
    });
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    marginBlock: "400",
    children: [/*#__PURE__*/_jsx(View, {
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(LearningContentCard, {
        item: cardModels[0],
        size: "small",
        overviewType: "academy-assistant",
        onItemClick: () => {
          if (contentType) {
            onContentCardClick({
              contentId: itemId,
              contentType,
              appearsIn: 'recommendation-card'
            });
            if (contentType === 'LESSON' || contentType === 'SHORT_FORM_CONTENT') {
              askAboutUpcomingContent(I18n.text('academy-ai-assistant.actions.videoContentAutoPrompt'), {
                mode: 'sidebar',
                source: 'recommendation-card'
              });
            }
          }
        }
      })
    }), isOnboardingFlow && contentType ? /*#__PURE__*/_jsx(OnboardingExploreMoreCta, {
      itemId: itemId,
      itemType: itemType,
      contentType: contentType,
      language: language,
      cardModel: cardModels[0]
    }) : null]
  });
};
export const AcademyRecommendations = ({
  pair
}) => {
  var _parsed$modelResult$d, _parsed;
  const output = pair.output;
  if (!output || output.type !== 'TEXT' || output.isError) return null;
  let parsed = null;
  try {
    parsed = JSON.parse(output.content);
  } catch (_unused) {
    return null;
  }
  const sections = (_parsed$modelResult$d = (_parsed = parsed) === null || _parsed === void 0 || (_parsed = _parsed.modelResult) === null || _parsed === void 0 ? void 0 : _parsed.dynamicResponseSections) !== null && _parsed$modelResult$d !== void 0 ? _parsed$modelResult$d : [];
  if (!sections.length) return null;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: sections.map(({
      id,
      responseSection
    }) => /*#__PURE__*/_jsx(RecommendationCard, {
      section: responseSection
    }, id))
  });
};